/**
 * When a newly downloaded build may be applied under the player's feet, and when it must
 * be offered instead.
 *
 * Pure: no service worker, no DOM, no store. The only import is a type. That is what
 * makes the rule testable without a browser, and keeps the decision reviewable in one
 * place instead of tangled into a callback.
 */

import type { Phase } from '../game/phase';

export type UpdateAction =
  /** Swap to the new build now. Invisible — the player is not in the world yet. */
  | 'apply'
  /** Tell the player it is ready and let them pick the moment. */
  | 'offer';

/**
 * Applying silently is only safe during `loading`: once the phase machine has reached
 * `playing` the player is in a level with no mid-level save, and a reload costs them the
 * session. `paused` is a live session too — the menu is not a fresh boot.
 *
 * `hasAppliedThisSession` is the reload-loop brake. If a deploy is observed half-synced,
 * the page that just auto-reloaded could detect an update again and reload again without
 * end; after one automatic apply, every later detection degrades to the notice.
 */
export function decideUpdateAction(phase: Phase, hasAppliedThisSession: boolean): UpdateAction {
  if (hasAppliedThisSession) return 'offer';
  return phase === 'loading' ? 'apply' : 'offer';
}
